import { formatMoney } from '@theme/money-formatting';
import { CartLinesUpdateEvent } from '@shopify/events';

const ENDPOINT = 'https://api.skio.com/storefront-http/get-dynamic-box';
const ESC = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' };
const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ESC[c]);
const fill = (s, vars) => String(s).replace(/\[(\w+)\]/g, (m, k) => (k in vars ? vars[k] : m));
const num = (x) => String(Math.round(x * 10) / 10);
const lastId = (gid) => String(gid || '').split('/').pop();
const CLOSE = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M4 4l8 8M12 4l-8 8"/></svg>';

class DrvStack extends HTMLElement {
  connectedCallback() {
    if (this.started) return;
    this.started = true;
    const data = JSON.parse(this.querySelector('[data-stack-data]').textContent);
    this.allPlans = data.plans || [];
    this.plans = [];
    this.t = data.strings;
    this.ticks = (data.ticks || []).map((s) => String(s).trim()).filter(Boolean);
    this.allTicks = (data.allTicks || []).map((s) => String(s).trim()).filter(Boolean);
    this.onceTicks = (data.onceTicks || []).map((s) => String(s).trim()).filter(Boolean);
    this.cards = [...this.querySelectorAll('[data-card]')].map((el) => ({
      el,
      foot: el.querySelector('[data-foot]'),
      variant: el.dataset.variant,
      title: el.dataset.title,
      price: Number(el.dataset.price),
      plans: JSON.parse(el.dataset.plans || '{}'),
      image: el.dataset.image,
      available: el.dataset.available === 'true',
      goals: (el.dataset.goals || '').split(' ').filter(Boolean),
    }));
    this.repeat = this.dataset.repeat === 'true';
    this.state = { qty: {}, goal: this.querySelector('[data-goal][aria-pressed="true"]')?.dataset.goal || 'all', mode: 'once', plan: '' };
    this.boxes = null;
    this.panel = this.querySelector('[data-panel]');
    this.sheet = this.querySelector('[data-sheet]');
    this.sheetPanel = this.querySelector('[data-sheet-panel]');
    this.bar = this.querySelector('[data-bar]');
    this.addEventListener('click', (e) => this.onClick(e));
    this.addEventListener('change', (e) => this.onChange(e));
    this.sheet?.addEventListener('click', (e) => {
      if (e.target === this.sheet) this.sheet.close();
    });
    this.load();
  }

  money(cents) {
    return formatMoney(Math.round(cents), this.dataset.moneyFormat, this.dataset.currency);
  }

  get box() {
    return this.boxes?.[this.state.mode] || null;
  }

  async fetchBox(productId) {
    const res = await fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ productPlatformId: productId }),
    });
    const box = (await res.json())?.DynamicBox;
    if (!box?.boxId) throw new Error('No box');
    const tiers = Object.entries(box.percentDiscountTier || {})
      .map(([n, p]) => [Number(n), Number(p)])
      .filter(([n, p]) => n > 0 && p > 0)
      .sort((a, b) => a[0] - b[0]);
    const sizes = (box.sizeInterval || []).map(Number).filter((n) => n > 0).sort((a, b) => a - b);
    const range = box.sizeRange || null;
    const min = range ? Number(range.min ?? range[0] ?? 1) : sizes[0] ?? tiers[0]?.[0] ?? 1;
    const max = range ? Number(range.max ?? range[1] ?? 99) : sizes[sizes.length - 1] ?? 99;
    const ids = new Set();
    (box.selectableProductVariants || []).forEach((p) => (p.productVariants || []).forEach((v) => ids.add(lastId(v.platformId))));
    const plans = Object.entries(box.sellingPlanGroup || {})
      .sort((a, b) => (a[1]?.position ?? 0) - (b[1]?.position ?? 0))
      .map(([gid]) => lastId(gid));
    return { id: box.boxId, tiers, sizes: range ? [] : sizes, min, max, ids, plans };
  }

  async load() {
    try {
      const subId = this.dataset.boxProduct;
      const onceId = this.dataset.onceBoxProduct;
      const [sub, once] = await Promise.all([
        this.fetchBox(subId),
        onceId && onceId !== subId ? this.fetchBox(onceId) : null,
      ]);
      this.boxes = { sub, once: once || sub };
      this.plans = sub.plans.map((id) => this.allPlans.find((p) => String(p.id) === id)).filter(Boolean);
      this.state.mode = this.dataset.mode === 'once' || !this.plans.length ? 'once' : 'sub';
      this.state.plan = this.plans.length ? String(this.plans[0].id) : '';
      this.bar.hidden = false;
      this.render();
    } catch (e) {
      this.panel.querySelector('[data-status]').textContent = this.t.error;
    }
  }

  inBox(card) {
    return Boolean(this.box?.ids.has(card.variant) && card.available);
  }

  count() {
    return Object.values(this.state.qty).reduce((a, b) => a + b, 0);
  }

  tierFor(n, box = this.box) {
    let pct = 0;
    (box?.tiers || []).forEach(([size, p]) => {
      if (n >= size) pct = p;
    });
    return pct;
  }

  saveAt(n, mode = this.state.mode) {
    const tier = this.tierFor(n, this.boxes[mode]);
    if (mode !== 'sub') return tier;
    return 100 - ((100 - this.planOff()) * (100 - tier)) / 100;
  }

  topSaving(mode = this.state.mode) {
    return this.saveAt(this.boxes[mode].max, mode);
  }

  canBuy(n) {
    return this.valid(n) && this.cards.every((c) => !this.state.qty[c.variant] || this.inBox(c));
  }

  valid(n) {
    if (!this.box) return false;
    return this.box.sizes.length ? this.box.sizes.includes(n) : n >= this.box.min && n <= this.box.max;
  }

  nextSize(n) {
    if (this.box.sizes.length) return this.box.sizes.find((s) => s > n) ?? null;
    return n < this.box.min ? this.box.min : null;
  }

  planPrice(card) {
    if (this.state.mode !== 'sub') return card.price;
    const p = card.plans[this.state.plan];
    return typeof p === 'number' ? p : card.price;
  }

  totals() {
    const n = this.count();
    const pct = this.tierFor(n);
    const lines = {};
    let items = 0;
    let after = 0;
    let total = 0;
    this.cards.forEach((c) => {
      const q = this.state.qty[c.variant] || 0;
      if (!q) return;
      const plan = q * this.planPrice(c);
      const line = { full: q * c.price, total: Math.round((plan * (100 - pct)) / 100) };
      lines[c.variant] = line;
      items += line.full;
      after += plan;
      total += line.total;
    });
    return { n, pct, lines, items, plan: items - after, stack: after - total, total };
  }

  planOff(plan = this.state.plan) {
    const c = this.cards.find((x) => typeof x.plans[plan] === 'number' && x.price > 0);
    if (!c) return 0;
    return Math.round(((c.price - c.plans[plan]) / c.price) * 1000) / 10;
  }

  bestSaving() {
    if (this.state.mode !== 'sub') return this.topSaving('once');
    const tier = this.tierFor(this.boxes.sub.max, this.boxes.sub);
    return Math.max(...this.plans.map((p) => 100 - ((100 - this.planOff(String(p.id))) * (100 - tier)) / 100));
  }

  fillHero() {
    const vars = { percent: num(this.bestSaving()), min: this.box.min, max: this.box.max };
    this.querySelectorAll('[data-fill]').forEach((el) => {
      el.dataset.fill = el.dataset.fill || el.textContent;
      el.textContent = fill(el.dataset.fill, vars);
      el.classList.add('is-filled');
    });
  }

  saving(tot) {
    if (!tot.items) return this.planOff();
    return Math.round(((tot.items - tot.total) / tot.items) * 1000) / 10;
  }

  struck(cents) {
    return `<s><span class="visually-hidden">${esc(this.t.regularPrice)} </span>${this.money(cents)}</s>`;
  }

  price(now, was) {
    const sale = was > now ? `<span class="visually-hidden">${esc(this.t.salePrice)} </span>` : '';
    return `<b>${sale}${this.money(now)}</b>`;
  }

  buyHTML(tot) {
    const t = this.t;
    const sub = this.state.mode === 'sub';
    const prices = tot.n
      ? `<span class="drv-stack__buy-prices">${this.price(tot.total, tot.items)}${tot.total < tot.items ? this.struck(tot.items) : ''}</span>`
      : '';
    const perks = this.plans.length
      ? this.ticks.map((x) =>
          sub ? `<li>${esc(x)}</li>` : `<li class="is-off"><span class="visually-hidden">${esc(t.notIncluded)} </span>${esc(x)}</li>`
        )
      : [];
    const own = sub ? [] : this.onceTicks;
    const lines = [...[...own, ...this.allTicks].map((x) => `<li>${esc(x)}</li>`), ...perks];
    const list = lines.length ? `<ul class="drv-stack__ticks">${lines.join('')}</ul>` : '';
    if (!this.plans.length) {
      return `<div class="drv-stack__buy" role="group" aria-label="${esc(t.howToBuy)}"><div class="drv-stack__buy-head"><span class="drv-stack__buy-title">${esc(t.once)}</span>${prices}</div>${list}</div>`;
    }
    const save = sub ? this.saving(tot) : 0;
    const planOptions = this.plans
      .map((p) => `<option value="${p.id}"${String(p.id) === this.state.plan ? ' selected' : ''}>${esc(p.days ? fill(t.every, { days: p.days }) : p.name)}</option>`)
      .join('');
    return `<div class="drv-stack__buy${sub ? ' is-sub' : ''}" role="group" aria-label="${esc(t.howToBuy)}">
        <div class="drv-stack__buy-head">
          <label class="drv-stack__switch${this.moved ? ' is-moved' : ''}"><input class="visually-hidden" type="checkbox" role="switch" data-mode aria-label="${esc(t.subscribe)}"${sub ? ' checked' : ''}><span class="drv-stack__switch-track" aria-hidden="true"></span><span class="drv-stack__buy-title">${esc(sub ? t.subscribe : t.once)}</span></label>
          <span class="drv-stack__buy-end">${save > 0 ? `<span class="drv-stack__save">${esc(fill(t.save, { percent: save }))}</span>` : ''}${prices}</span>
        </div>
        ${sub ? `<span class="drv-stack__select-wrap"><select class="drv-stack__select" data-plan aria-label="${esc(t.delivery)}">${planOptions}</select></span>` : ''}
        ${list}
      </div>`;
  }

  message(n) {
    const t = this.t;
    const first = this.box.tiers[0]?.[0] ?? this.box.min;
    if (n === 0) return fill(t.startSaving, { count: first, percent: num(this.saveAt(first)) });
    const next = this.box.tiers.find(([size]) => size > n);
    if (!next) return fill(t.maxTier, { percent: num(this.saveAt(n)) });
    const more = next[0] - n;
    return fill(this.tierFor(n) ? t.nextTier : t.oneMore, { count: more, percent: num(this.saveAt(next[0])), current: num(this.saveAt(n)) });
  }

  ctaText(tot) {
    if (this.valid(tot.n)) return fill(this.t.cta, { price: this.money(tot.total) });
    const next = this.nextSize(tot.n);
    return fill(this.t.ctaMore, { count: next ? next - tot.n : 1 });
  }

  renderTiers() {
    const el = this.querySelector('[data-tiers]');
    if (el && this.box.tiers.length) {
      el.innerHTML = this.box.tiers
        .map(([size]) => `<span class="drv-stack__tier"><b>${num(this.saveAt(size))}%</b>${esc(fill(this.t.tierItems, { count: size }))}</span>`)
        .join('');
      el.hidden = false;
    }
    const top = this.topSaving();
    this.querySelectorAll('[data-upto]').forEach((s) => {
      s.textContent = top > 0 ? fill(this.t.upTo, { percent: num(top) }) : '';
    });
  }

  render() {
    const n = this.count();
    const full = n >= this.box.max;
    this.fillHero();
    this.renderTiers();
    this.querySelectorAll('[data-goal]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.goal === this.state.goal)));
    this.cards.forEach((c) => {
      const q = this.state.qty[c.variant] || 0;
      c.el.hidden = !(this.state.goal === 'all' || c.goals.includes(this.state.goal));
      c.el.classList.toggle('is-in', q > 0);
      if (q && !this.repeat) {
        c.foot.innerHTML = `<div class="drv-stack__stepper drv-stack__stepper--one">
          <span>${esc(this.t.inStackTag)}</span>
          <button type="button" class="drv-stack__step" data-dec="${c.variant}" aria-label="${esc(fill(this.t.removeOne, { title: c.title }))}">${CLOSE}</button>
        </div>`;
      } else if (q) {
        c.foot.innerHTML = `<div class="drv-stack__stepper">
          <button type="button" class="drv-stack__step" data-dec="${c.variant}" aria-label="${esc(fill(this.t.removeOne, { title: c.title }))}">−</button>
          <span>${q}<span class="drv-stack__step-label"> ${esc(this.t.inStack)}</span></span>
          <button type="button" class="drv-stack__step" data-inc="${c.variant}" aria-label="${esc(fill(this.t.addOne, { title: c.title }))}"${full ? ' disabled' : ''}>+</button>
        </div>`;
      } else {
        const inBox = this.inBox(c);
        c.foot.innerHTML = `<button type="button" class="drv-stack__btn" data-inc="${c.variant}"${!inBox || full ? ' disabled' : ''}>${esc(full && inBox ? this.t.full : this.t.add)}</button>`;
      }
    });
    this.renderPanels();
  }

  panelHTML() {
    const t = this.t;
    const tot = this.totals();
    const steps = this.box.tiers
      .map(([size]) => `<span class="drv-stack__step-tier${tot.n >= size ? ' is-on' : ''}"><span class="drv-stack__tier-pct">${num(this.saveAt(size))}%</span><span class="drv-stack__tier-bar"></span><span class="drv-stack__tier-items">${esc(fill(t.tierItems, { count: size }))}</span></span>`)
      .join('');
    const rows = this.cards
      .filter((c) => this.state.qty[c.variant])
      .map((c) => {
        const q = this.state.qty[c.variant];
        const line = tot.lines[c.variant];
        const meta = `${q}× ${line.total < line.full ? this.struck(line.full) : ''}${this.price(line.total, line.full)}`;
        return `<li class="drv-stack__item">${c.image ? `<img src="${esc(c.image)}" alt="" width="44" height="44" loading="lazy">` : '<span class="drv-stack__item-img"></span>'}
          <span class="drv-stack__item-text"><span class="drv-stack__item-name">${esc(c.title)}</span><span class="drv-stack__item-meta">${meta}</span></span>
          <button type="button" class="drv-stack__icon-btn" data-dec="${c.variant}" aria-label="${esc(fill(t.removeOne, { title: c.title }))}">${CLOSE}</button></li>`;
      })
      .join('');
    const slot = tot.n < this.box.max ? `<li class="drv-stack__slot">${esc(tot.n ? t.addAnother : t.addFirst)}</li>` : '';
    const plan = this.plans.find((p) => String(p.id) === this.state.plan);
    const fine = (this.state.mode === 'sub' && plan?.days ? fill(t.billed, { days: plan.days }) + ' ' : '') + t.atCheckout;
    return `<div class="drv-stack__panel-head"><p class="drv-stack__panel-title">${esc(t.yourStack)}</p><span class="drv-stack__count">${esc(fill(t.count, { count: tot.n, max: this.box.max }))}</span></div>
      ${steps ? `<div class="drv-stack__track">${steps}</div>` : ''}
      <p class="drv-stack__msg">${esc(this.message(tot.n))}</p>
      <ul class="drv-stack__items" aria-label="${esc(t.itemsLabel)}">${rows}${slot}</ul>
      ${this.buyHTML(tot)}
      <div class="drv-stack__sums">
        <div><span>${esc(fill(t.items, { count: tot.n }))}</span><span>${this.money(tot.items)}</span></div>
        ${tot.plan > 0 ? `<div class="drv-stack__minus"><span>${esc(t.subscription)}</span><span>−${this.money(tot.plan)}</span></div>` : ''}
        ${tot.stack > 0 ? `<div class="drv-stack__minus"><span>${esc(fill(t.stackSaving, { percent: tot.pct }))}</span><span>−${this.money(tot.stack)}</span></div>` : ''}
        <div class="drv-stack__total"><span>${esc(t.total)}</span><span>${tot.total < tot.items ? this.struck(tot.items) : ''}${this.price(tot.total, tot.items)}</span></div>
      </div>
      <button type="button" class="drv-stack__btn" data-cta${this.canBuy(tot.n) && !this.busy ? '' : ' disabled'}>${esc(this.busy ? t.adding : this.ctaText(tot))}</button>
      <p class="drv-stack__fine">${esc(fine)}</p>
      <p class="drv-stack__error" data-error hidden></p>`;
  }

  renderPanels() {
    this.panel.innerHTML = this.panelHTML();
    if (this.sheet?.open) this.sheetPanel.innerHTML = this.panelHTML();
    const tot = this.totals();
    this.querySelector('[data-bar-text]').textContent = tot.n
      ? fill(this.t.barItems, { count: tot.n, price: this.money(tot.total) })
      : this.t.empty;
    this.querySelector('[data-mini]').innerHTML = this.box.tiers
      .map(([size]) => `<i class="${tot.n >= size ? 'is-on' : ''}"></i>`)
      .join('');
    const cta = this.bar.querySelector('[data-cta]');
    if (cta) {
      cta.textContent = this.busy ? this.t.adding : this.ctaText(tot);
      cta.disabled = !this.canBuy(tot.n) || this.busy;
    }
  }

  say(text) {
    const live = this.querySelector('[data-live]');
    if (live) live.textContent = text;
  }

  onClick(e) {
    const b = e.target.closest('button');
    if (!b || !this.box) return;
    if (b.dataset.goal) {
      this.state.goal = b.dataset.goal;
      this.render();
      return;
    }
    if (b.dataset.inc) {
      const card = this.cards.find((c) => c.variant === b.dataset.inc);
      if (!card || !this.inBox(card) || this.count() >= this.box.max) return;
      if (!this.repeat && this.state.qty[card.variant]) return;
      this.state.qty[card.variant] = (this.state.qty[card.variant] || 0) + 1;
      this.render();
      this.say(fill(this.t.added, { title: card.title, count: this.count(), max: this.box.max }));
      return;
    }
    if (b.dataset.dec) {
      const v = b.dataset.dec;
      this.state.qty[v] = Math.max(0, (this.state.qty[v] || 0) - 1);
      if (!this.state.qty[v]) delete this.state.qty[v];
      this.render();
      this.say(fill(this.t.removed, { count: this.count(), max: this.box.max }));
      return;
    }
    if (b.hasAttribute('data-cta')) {
      this.addStack();
      return;
    }
    if (b.hasAttribute('data-open')) {
      this.sheetPanel.innerHTML = this.panelHTML();
      this.sheet.showModal();
      return;
    }
    if (b.hasAttribute('data-close')) this.sheet.close();
  }

  onChange(e) {
    const panel = e.target.closest('[data-panel], [data-sheet-panel]');
    if (e.target.matches('[data-mode]')) {
      this.state.mode = e.target.checked ? 'sub' : 'once';
      this.moved = true;
      this.render();
      this.moved = false;
      panel?.querySelector('[data-mode]')?.focus();
    }
    if (e.target.matches('[data-plan]')) {
      this.state.plan = e.target.value;
      this.render();
      panel?.querySelector('[data-plan]')?.focus();
    }
  }

  getCart() {
    return fetch(`${this.dataset.cartUrl}.js`, { headers: { Accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : null))
      .catch(() => null);
  }

  async addStack() {
    const n = this.count();
    if (!this.canBuy(n) || this.busy) return;
    this.busy = true;
    this.renderPanels();
    const toCheckout = this.dataset.afterAdd === 'checkout';
    const fromSheet = Boolean(this.sheet?.open);
    let added = null;
    let before = null;
    const settle = async (didError) => {
      const cart = await this.getCart();
      added.resolve({
        cart: cart ? CartLinesUpdateEvent.createCartFromAjaxResponse(cart) : null,
        detail: {
          didError,
          items: cart?.items || [],
          itemCount: cart ? cart.item_count : (before?.item_count || 0) + (didError ? 0 : n),
          source: 'drv-stack',
        },
      });
    };
    try {
      before = await this.getCart();
      const used = new Set(
        (before?.items || [])
          .filter((i) => i.properties && i.properties._dynamicBoxId === this.box.id)
          .map((i) => Number(i.properties._dynamicBoxIndex))
      );
      let index = 0;
      while (used.has(index)) index += 1;
      const items = Object.entries(this.state.qty).map(([variant, quantity]) => {
        const line = { id: Number(variant), quantity, properties: { _dynamicBoxId: this.box.id, _dynamicBoxIndex: index } };
        if (this.state.mode === 'sub' && this.state.plan) line.selling_plan = Number(this.state.plan);
        return line;
      });
      if (!toCheckout) {
        this.sheet?.close();
        added = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: items.map((i) => ({ merchandiseId: String(i.id), quantity: i.quantity })),
            promise: added.promise,
          })
        );
      }
      const res = await fetch(`${this.dataset.cartAddUrl}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.description || body.message || this.t.addError);
      }
      if (toCheckout) {
        window.location.href = '/checkout';
        return;
      }
      await settle(false);
      this.busy = false;
      this.state.qty = {};
      this.render();
    } catch (err) {
      if (added) settle(true);
      this.busy = false;
      if (this.sheet && !this.sheet.open && (fromSheet || !this.panel.offsetParent)) this.sheet.showModal();
      this.renderPanels();
      this.querySelectorAll('[data-error]').forEach((p) => {
        p.textContent = err.message || this.t.addError;
        p.hidden = false;
      });
    }
  }
}

if (!customElements.get('drv-stack')) customElements.define('drv-stack', DrvStack);
